import { action, computed, makeObservable, observable, ObservableMap, ObservableSet } from 'mobx';
import { Store } from '../../../store/store';
import { StoreNode } from '../../../store/storeNode';
import { LineSeriesProps } from './xySeriesChartSchema';

type DataKey = string;
type Color = string;
type XYSeriesChartStateProps = {
  series?: LineSeriesProps[];
  colors?: string[];
};

export class XYSeriesChartState extends StoreNode {
  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      initialSeriesArr: computed,
      availableSeriesArr: computed,
      availableColorsArr: computed,
      mounted: action,
      unmounted: action,
      loadSeries: action,
      loadColors: action,
      handleClickLegendItem: action,
      toggleDatum: action,
    });
  }

  readonly initialSeries: ObservableMap<DataKey, LineSeriesProps> =
    observable.map<DataKey, LineSeriesProps>();
  readonly initialColors: ObservableMap<DataKey, Color> =
    observable.map<DataKey, Color>();
  readonly selectedKeys: ObservableSet<DataKey> = observable.set<DataKey>();

  get initialSeriesArr(): LineSeriesProps[] {
    return [...this.initialSeries.values()];
  }

  get availableSeriesArr(): LineSeriesProps[] {
    const newSeries = new Map<DataKey, LineSeriesProps>(this.initialSeries);

    this.selectedKeys.forEach((key) => {
      newSeries.delete(key);
    });

    return [...newSeries.values()];
  }

  get availableColorsArr(): Color[] {
    const newColors = new Map<DataKey, Color>(this.initialColors);

    this.selectedKeys.forEach((key) => {
      newColors.delete(key);
    });

    return [...newColors.values()];
  }

  mounted({ series, colors }: XYSeriesChartStateProps) {
    this.loadSeries(series || []);
    this.loadColors(colors || []);
  }

  unmounted() {
    this.selectedKeys.clear();
  }

  loadSeries(series: LineSeriesProps[]) {
    this.initialSeries.replace(series.map((data) => [data.dataKey, data]));
  }

  loadColors(colors: Color[]) {
    const colorsSet = new Set(colors);
    const colorsIterator = colorsSet.values();

    this.initialSeries.forEach(({ dataKey }) => {
      const nextColor = colorsIterator.next().value;
      if (nextColor)
        this.initialColors.set(dataKey, nextColor);
    });
  }

  handleClickLegendItem = (selectedKey: string) => {
    this.toggleDatum(selectedKey);
  };

  toggleDatum(selectedKey: string) {
    if (this.selectedKeys.has(selectedKey)) {
      this.selectedKeys.delete(selectedKey);
    } else {
      this.selectedKeys.add(selectedKey);
    }
  }
}
